import { useDispatch } from 'react-redux'
import {
  Optional,
  Resume,
  SingleTemplateSettings,
  TemplateSettings,
  UserDocumentTemplateId,
} from 'packages/types'
import { actions, UpdateTemplateSettingsParams } from 'builder/modules/resumeEditor'
import { camelCase } from 'builder/utils/camelCase'
import { useTemplateCustomization } from './useTemplateCustomization'

export const getUpdatedSettings = <
  T extends UserDocumentTemplateId,
  S extends keyof SingleTemplateSettings,
>(
  templateName: Optional<T>,
  params: UpdateTemplateSettingsParams<S>,
  templatesSettings: Optional<TemplateSettings>,
) => {
  const { settingsName, value } = params

  if (!templateName) {
    return null
  }

  const convertedTemplateName = camelCase(templateName)
  const updatedValue: TemplateSettings = {
    ...templatesSettings,
    [convertedTemplateName]: {
      ...(templatesSettings ? templatesSettings[convertedTemplateName] : {}),
      [settingsName]: value,
    },
  }

  return updatedValue
}

export const useUpdateTemplateSettings = (resume: Optional<Resume>) => {
  const dispatch = useDispatch()
  const { templatesSettings } = useTemplateCustomization(resume)

  const updateTemplateSettings = <S extends keyof SingleTemplateSettings>(
    params: UpdateTemplateSettingsParams<S>,
  ) => {
    const { settingsName, value } = params
    const updatedValue = getUpdatedSettings(
      resume?.template,
      { settingsName, value },
      templatesSettings,
    )

    if (!updatedValue) {
      return
    }

    dispatch(
      actions.updateSimpleField({
        name: 'templateSettings',
        value: updatedValue,
        debounce: true,
      }),
    )
  }

  return {
    updateTemplateSettings,
  }
}
